/**
 * Default tags available for support cases
 */
export const DEFAULT_TAGS = [
  'Additional Resources',
  'Application Issue',
  'Eligibility',
  'Liveness Issue',
  'Payment',
  'Recollect'
];

/**
 * Get initials from a user name string
 * @param str - User name string (may include browser tab after /)
 * @returns Initials (e.g., "John Doe" -> "JD")
 */
export const getInitials = (str: string): string => {
  if (str) {
    return str
      .split('/')[0]
      .split(' ')
      .filter((v: string) => v)
      .map((w: string) => w[0])
      .join('');
  }
  return '';
};

/**
 * Determine the application stage from an applicant UID
 * @param uid - Applicant UID
 * @param localization - Localization object with application_stage strings
 * @returns Localized stage string
 */
export const getStageFromUid = (
  uid: string | undefined,
  localization: { pre_application: string; applied: string; in_progress: string }
): string => {
  if (!uid) return localization.pre_application;
  // Length <= 22 means it's an ingested UID
  if (uid.length <= 22) return localization.applied;
  return localization.in_progress;
};

/**
 * Get display name for an assignee, adding "(AidKit)" suffix for internal employees
 * @param assignee - Assignee object with uid, name, and email
 * @returns Display name with optional (AidKit) suffix
 */
export const getAssigneeDisplayName = (assignee: { uid: string; name: string; email: string }): string => {
  if (!assignee.email || !(assignee.email.includes('@aidkit.org') || assignee.email.includes('@aidkit.cloud'))) {
    return assignee.name;
  }

  return `${assignee.name} (AidKit)`;
};

/**
 * Creates fallback contact info from caseInfo.contact when applicantInfo lacks contact data
 * Matches pattern from SupportCase.tsx
 * @param applicantInfo - Applicant information dictionary
 * @param caseContact - Contact string from case info
 * @returns InfoDict with contact information
 */
export const getContactInfoWithFallback = (
  applicantInfo: any,
  caseContact: string
): any => {
  // Check if applicantInfo has actual contact information (email or phone)
  const hasContactInfo = applicantInfo?.['email'] || applicantInfo?.['phone_number'] || applicantInfo?.['phone'];

  if (hasContactInfo) {
    return applicantInfo;
  }

  // Fallback to caseInfo.contact
  return caseContact.includes("@")
    ? { email: caseContact }
    : { phone_number: caseContact };
};

/**
 * Determines if WhatsApp can be sent based on contact info and message history
 * @param whatsAppKey - Key to check in contact info
 * @param contactInfo - Contact information dictionary
 * @param messages - Array of messages
 * @returns Whether WhatsApp can be sent
 */
export const checkCanSendWhatsApp = (
  whatsAppKey: string,
  contactInfo: any,
  messages: any[]
): boolean => {
  if (whatsAppKey && contactInfo?.[whatsAppKey] === 'yes') {
    return true;
  }

  const applicantSentWhatsApp = messages?.find(m => m.kind === 'whatsapp');
  return !!applicantSentWhatsApp;
};

/**
 * Gets the most recent inbound WhatsApp message from message list
 * @param messages - Array of messages
 * @returns Most recent inbound WhatsApp message or undefined
 */
export const getLatestInboundWhatsAppMessage = (messages: any[]): any | undefined => {
  return messages?.filter(msg => {
    return msg.kind === 'whatsapp' && (msg.needs_attention || msg.status);
  })?.sort((msg1, msg2) => {
    return (new Date(msg2.created_at)).getTime() - (new Date(msg1.created_at)).getTime()
  })?.[0];
};

/**
 * Checks if the 24-hour WhatsApp messaging window has closed
 * @param latestInboundMessage - Most recent inbound WhatsApp message
 * @returns Whether the window is closed
 */
export const isWhatsAppWindowClosed = (latestInboundMessage: any): boolean => {
  if (!latestInboundMessage) {
    return false;
  }

  const latestDate = (new Date(latestInboundMessage.created_at)).getTime();
  const timeDifference = (new Date()).getTime() - latestDate;
  const twentyFourHours = 24 * 60 * 60 * 1000;

  return timeDifference >= twentyFourHours;
};

/**
 * Maps a message kind to a communication channel
 * @param kind - Message kind string
 * @returns Channel type or null
 */
export const mapMessageKindToChannel = (
  kind: string
): 'email' | 'sms' | 'whatsapp' | null => {
  if (kind === 'email') {
    return 'email';
  } else if (kind === 'sms' || kind === 'mms' || kind === 'call') {
    return 'sms';
  } else if (kind === 'whatsapp') {
    return 'whatsapp';
  }
  return null;
};

/**
 * Determines available communication channels based on contact info
 * @param contactInfo - Contact information dictionary
 * @param canSendWhatsApp - Whether WhatsApp is available
 * @param alternateContacts - Array of alternate contact configurations
 * @returns Array of available channels
 */
export const getAvailableChannels = (
  contactInfo: any,
  canSendWhatsApp: boolean,
  alternateContacts: any[]
): ('email' | 'sms' | 'whatsapp')[] => {
  const channels: ('email' | 'sms' | 'whatsapp')[] = [];

  // Check if email is available for ANY contact
  const hasEmail = contactInfo?.['email'] ||
    alternateContacts.some((ac: any) => contactInfo?.[ac.email_key]);
  if (hasEmail) {
    channels.push('email');
  }

  // Check if phone/SMS is available for ANY contact
  const hasPhone = contactInfo?.['phone_number'] ||
    alternateContacts.some((ac: any) => contactInfo?.[ac.phone_key]);
  if (hasPhone) {
    channels.push('sms');
  }

  // Check if WhatsApp is available (must have phone AND canSendWhatsApp)
  if (hasPhone && canSendWhatsApp) {
    channels.push('whatsapp');
  }

  return channels;
};

/**
 * Builds "Via [channels]" display text from message list
 * @param messages - Array of messages
 * @param L - Localization object
 * @returns Formatted channel text string
 */
export const buildChannelText = (
  messages: any[],
  L: any
): string => {
  const uniqueKinds = new Set(messages.map(msg => msg.kind));
  const channels: string[] = [];

  if (uniqueKinds.has('email')) channels.push(L.applicant.comms.email);
  if (uniqueKinds.has('sms') || uniqueKinds.has('mms')) channels.push(L.applicant.comms.sms);
  if (uniqueKinds.has('call')) channels.push(L.applicant.comms.call);
  if (uniqueKinds.has('whatsapp')) channels.push(L.applicant.comms.whatsapp);

  if (channels.length === 0) return '';

  // Join with "and" for the last item
  if (channels.length === 1) {
    return `${L.applicant.comms.via} ${channels[0]}`;
  } else if (channels.length === 2) {
    return `${L.applicant.comms.via} ${channels[0]} ${L.applicant.comms.and} ${channels[1]}`;
  } else {
    const lastChannel = channels.pop();
    return `${L.applicant.comms.via} ${channels.join(', ')}, ${L.applicant.comms.and} ${lastChannel}`;
  }
};
